import { omit } from "lodash";
import { COUNTRY } from "@type/country";
import {
  BRAND_LIST,
  GABrandList,
  LoginStateType,
  ProductInfoType,
  SiteType
} from "@type/index";
import {
  checkPreviousPageExists,
  getIsECGuestLoggedIn,
  getIsLoggedIn,
  getRegionFromUrl,
  getSub
} from "@util/common";
import { isEUCountry } from "@util/country";

export const isClient = typeof window !== "undefined";

export const TRACK_EVENT = "trackEvent";
export const TRACK_EVENT_SEARCH = "trackEventSearch";
export const SEARCH_V2 = "search_26v2";
export const ADD_TO_WISHLIST = "add_to_wishlist";
export const REMOVE_FROM_WISHLIST = "remove_from_wishlist";
export const SELECT_ITEM = "select_item";

export type GATrackEventType = {
  _clear?: boolean;
  brand?: GABrandList;
  event?: string;
  value?: number;
  eventAction?: string;
  eventCategory?: string;
  eventLabel?: string;
  eventValue?: string;
  itemIndex?: string;
  itemId?: string;
  searchTerm?: any;
  resultsNumber?: string;
  genderFilter?: string;
  categoryFilter?: any;
  sizeFilter?: any;
  heightFilter?: any;
  search_type?: string;
  search_type_detail?: string;
  search_location?: string;
  search_term?: string;
  search_term_raw?: string;
  result_count?: number;
};

export type GAGlobalOptionType = {
  event: string;
  ecommerce: eCommerceType;
};

export type eCommerceType = {
  currency?: string;
  value?: number;
  items?: Array<GAeCommerceItem>;
  custom_item_id?: string;
  custom_item_product_id?: string;
  custom_item_l1_id?: string;
};

export type GAeCommerceItem = {
  item_id: string;
  item_product_id: string;
  item_l1_id: string;
  item_name: string;
  index: number;
  item_brand: BRAND_LIST;
  item_category: string;
  item_category2: string;
  item_category3: string;
  item_category4: string;
  item_list_name: string;
  item_list_type: string;
  item_variant: string;
  item_color_code: string;
  price: number;
  item_size: string;
  item_img_id?: string;
  item_oos?: string;
  item_search_function?: string;
  prc_grp_seq_num: string;
  mk_product_id: string;
};

export type GATrackPageView = {
  event: string;
  pageUrlPath: string;
  pageTitle: string;
  previousPageExists: boolean;
  brand: string;
  siteType: SiteType;
  pageType: string;
  content_group?: string;
  subID: string;
  loginState: LoginStateType;
};

/**
 * Track an event such as clicking event on GoogleAnalytics
 * via sending data to dataLayer of window object.
 *
 * @param data - Analytics data
 * @param clearEcommerce - Optional boolean to clear previous ecommerce object before pushing event (default: false)
 */
export const sendTrackEvent = (
  data: GATrackEventType | GAGlobalOptionType,
  clearEcommerce = false
): void => {
  if (!isClient) {
    return;
  }
  if (!window.dataLayer) {
    return;
  }
  if (isEUCountry(getRegionFromUrl())) {
    return;
  }

  const eventData = {
    ...data,
    event: data.event || "trackEvent"
  };

  if (
    window.UQCustomerAppAndroid &&
    window.UQCustomerAppAndroid.receiveAnalyticsData
  ) {
    // Android App
    window.UQCustomerAppAndroid.receiveAnalyticsData(JSON.stringify(eventData));
  } else if (
    window.webkit &&
    window.webkit.messageHandlers.receiveAnalyticsData &&
    window.webkit.messageHandlers.receiveAnalyticsData.postMessage
  ) {
    // iOS App
    window.webkit.messageHandlers.receiveAnalyticsData.postMessage(eventData);
  }
  // Web Browser and Native App
  if (clearEcommerce) {
    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
  }
  window.dataLayer.push(data);
};

/**
 * sendPageViewForWave2 passes data into window.dataLayer and run callback functions which are injected by native apps if needed.
 * It is called for tracking page view events and virtual page view events.
 *
 * @param data
 */
export const sendPageViewForWave2 = (data: any) => {
  if (!isClient) {
    return;
  }
  if (!window.dataLayer) {
    return;
  }
  if (isEUCountry(getRegionFromUrl())) {
    return;
  }

  if (
    window.UQCustomerAppAndroid &&
    window.UQCustomerAppAndroid.receiveAnalyticsData
  ) {
    // Android App
    window.UQCustomerAppAndroid.receiveAnalyticsData(JSON.stringify(data));
  } else if (
    window.webkit &&
    window.webkit.messageHandlers.receiveAnalyticsData &&
    window.webkit.messageHandlers.receiveAnalyticsData.postMessage
  ) {
    // iOS App
    window.webkit.messageHandlers.receiveAnalyticsData.postMessage(data);
  }
  // Web Browser and Native App
  window.dataLayer.push(data);
};

export const trackPageView = (baseOption: GATrackPageView) => {
  sendPageViewForWave2({
    ...baseOption
  });
};

export const getPageViewBaseOption = (
  pageTitle: string,
  brand: string,
  extraData?: Record<string, unknown>
): GATrackPageView => ({
  event: "trackPageView",
  pageUrlPath: `${window.location.pathname}${window.location.search}${window.location.hash}`,
  pageTitle: pageTitle,
  previousPageExists: checkPreviousPageExists(),
  brand: brand,
  siteType: SiteType.Responsive,
  pageType: "imagesearch",
  content_group: "search",
  subID: getSub() || "",
  loginState: getLoginState(),
  ...(extraData && extraData)
});

export const trackSearchClickEvent = (resultsCount?: number) => {
  sendTrackEvent({
    event: SEARCH_V2,
    search_type: "product",
    search_type_detail: "image",
    search_location: "search",
    search_term: "image_upload",
    search_term_raw: "",
    result_count: resultsCount || 0
  });
};

export const getBaseGAItemProps = ({
  brand,
  product,
  index
}: {
  brand: BRAND_LIST;
  product: ProductInfoType;
  index?: number | null;
}) => ({
  item_id: product?.itemId || "",
  item_product_id: product?.productId || "",
  item_l1_id: product?.l1Id || "",
  item_name: product?.name || "",
  index: index != null ? index + 1 : 0,
  item_brand: brand || "",
  item_category: product?.sizeGender || product?.genderCategory || "",
  item_category2: "",
  item_category3: "",
  item_category4: "",
  item_list_name: "",
  item_list_type: "",
  item_variant: product?.wishColorName?.toLocaleLowerCase() || "",
  item_color_code: product?.wishColorCode || "",
  price: product?.price ?? 0,
  item_size: product?.wishSize || "",
  item_img_id: product?.imageUrl?.split("/").pop()?.replace(/\?.*$/, "") || "",
  item_search_function: "",
  prc_grp_seq_num: product?.priceGroup || "",
  mk_product_id: getCommunicationCodeForGA(product) || ""
});

export const getGAPropsForWishlist = (
  brand: BRAND_LIST,
  product: ProductInfoType,
  index: number
) => ({
  ...getBaseGAItemProps({
    brand,
    product,
    index
  }),
  item_oos: ""
});

export type GAWishEventType = {
  brand: BRAND_LIST;
  country: COUNTRY;
  productData: ProductInfoType;
};

export const trackAddToWishListEvent = (data: GAWishEventType) =>
  sendTrackEvent(
    {
      event: ADD_TO_WISHLIST,
      ecommerce: {
        currency: data.productData?.currency || "",
        value: data.productData?.price ?? 0,
        items: [
          {
            ...omit(
              getGAPropsForWishlist(
                data.brand,
                data.productData,
                data.productData?.index ?? 0
              ),
              ["item_search_function"]
            )
          }
        ]
      }
    },
    true
  );

export const trackRemoveFromWishListEvent = (data: GAWishEventType) =>
  sendTrackEvent(
    {
      event: REMOVE_FROM_WISHLIST,
      ecommerce: {
        currency: data.productData?.currency || "",
        value: data.productData?.price ?? 0,
        custom_item_id: data.productData?.itemId || "",
        custom_item_product_id: data.productData?.productId || "",
        custom_item_l1_id: data.productData?.l1Id || "",
        items: [
          {
            ...omit(
              getGAPropsForWishlist(
                data.brand,
                data.productData,
                data.productData?.index ?? 0
              ),
              ["item_search_function", "item_img_id"]
            )
          }
        ]
      }
    },
    true
  );

export const getCommunicationCodeForGA = (product: ProductInfoType): string => {
  const productCode = product?.productId?.split("-")[0] ?? "";
  const colorCode = product?.wishColorCode ?? "";
  const sizeCode = product?.wishSizeCode ?? "";
  const lengthCode = product?.pld?.displayCode ?? "000";
  if (productCode && colorCode && sizeCode && lengthCode) {
    return `${productCode}-${colorCode}-${sizeCode}-${lengthCode}`;
  }
  return "";
};

export const getLoginState = (): LoginStateType => {
  if (!isClient) {
    return LoginStateType.NotLogin;
  }
  if (getIsLoggedIn()) {
    return LoginStateType.EC;
  }
  if (getIsECGuestLoggedIn()) {
    return LoginStateType.Guest;
  }
  return LoginStateType.NotLogin;
};

export const trackSelectItemEvent = (data: GAWishEventType) =>
  sendTrackEvent(
    {
      event: SELECT_ITEM,
      ecommerce: {
        items: [
          {
            ...getBaseGAItemProps({
              brand: data.brand,
              product: data.productData,
              index: data.productData?.index ?? 0
            })
          }
        ]
      }
    },
    true
  );
